/**
 * 1.4.2: 邀请链接接受页面
 * 用户点击邀请链接后显示此页面，验证邀请并加入组织
 * 支持未登录用户直接在页面内登录/注册
 * 1.5.6: 改进多窗口场景 - 当邀请已被另一个窗口处理时，显示"已成功加入"
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Building2, 
  Users, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ChevronDown
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  checkInvitation, 
  acceptInvitation,
  setCurrentOrganization,
  getUserOrganizations,
  type InvitationInfo,
  type UserOrganization
} from '../services/organizationService';
import { 
  getCurrentUser, 
  signInWithEmail, 
  signUpWithEmail, 
  signInWithGoogle,
  onAuthStateChange
} from '../services/authService';
import { translateApiError, translateAuthError } from '../utils/errorTranslation';
import { validatePasswordStrength, PASSWORD_MIN_LENGTH } from '../utils/passwordValidation';
import type { User } from '@supabase/supabase-js';

// 1.4.2: 语言切换组件 - 复用 LandingPage 设计
const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'zh', label: '中文', abbr: '中' },
    { code: 'ja', label: '日本語', abbr: 'あ' },
    { code: 'en', label: 'English', abbr: 'A' }
  ];

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/80 hover:bg-white shadow-sm transition-colors"
      >
        <span className="w-6 h-6 flex items-center justify-center bg-gradient-to-br from-primary to-purple-700 text-white rounded-md text-xs font-semibold">
          {currentLang.abbr}
        </span>
        <span className="text-sm font-medium text-gray-700">{currentLang.label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[140px] z-50">
          {languages.map(lang => (
            <button
              key={lang.code}
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors ${
                lang.code === i18n.language ? 'bg-purple-50 text-purple-600' : 'text-gray-700'
              }`}
            >
              <span className={`w-6 h-6 flex items-center justify-center rounded-md text-xs font-semibold ${
                lang.code === i18n.language 
                  ? 'bg-gradient-to-br from-primary to-purple-700 text-white' 
                  : 'bg-purple-100 text-purple-600'
              }`}>
                {lang.abbr}
              </span>
              <span className="text-sm">{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export function InviteAcceptPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<User | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isCheckingInvite, setIsCheckingInvite] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<InvitationInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // 1.5.6: 新增状态 - 用于处理多窗口场景，当邀请已被另一个窗口处理时
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const [joinedOrgInfo, setJoinedOrgInfo] = useState<UserOrganization | null>(null);
  
  // 1.5.4: 使用 ref 跟踪是否已发起接受邀请，避免 useEffect 依赖数组中的 stale closure 问题
  const acceptanceInitiatedRef = useRef(false);
  
  // 登录/注册表单状态
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const passwordChecks = useMemo(() => validatePasswordStrength(password).checks, [password]);

  // 检查用户登录状态
  useEffect(() => {
    const checkAuth = async () => {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setIsCheckingAuth(false);
    };
    checkAuth();
    
    // 监听认证状态变化
    // 1.5.3: 只更新 user 状态，自动接受邀请逻辑移到单独的 useEffect
    const { unsubscribe } = onAuthStateChange(async (currentUser) => {
      setUser(currentUser);
    });
    
    return () => unsubscribe();
  }, []);

  // 1.5.3: 自动接受邀请 - 当用户登录且邀请有效时自动接受
  // 1.5.4: 修复 exhaustive-deps 规则违反问题
  // 使用 ref 跟踪是否已发起接受，避免 stale closure 和重复触发
  useEffect(() => {
    if (user && token && inviteInfo?.valid && !acceptanceInitiatedRef.current) {
      acceptanceInitiatedRef.current = true;
      handleAcceptInvitation();
    }
  }, [user, token, inviteInfo?.valid]);

  // 检查邀请链接有效性
  // 1.5.6: 当邀请失效时，检查用户是否已是组织成员（处理多窗口场景）
  useEffect(() => {
    const verifyInvitation = async () => {
      if (!token) {
        setError(t('invite.invalidInviteLink', '无效的邀请链接'));
        setIsCheckingInvite(false);
        return;
      }

      const { info, error: checkError } = await checkInvitation(token);
      
      if (checkError || !info?.valid) {
        // 1.5.6: 邀请链接失效时，检查用户是否已经是组织成员
        // 这种情况通常发生在多窗口场景：另一个窗口已经成功处理了邀请
        const errorMsg = checkError?.message || info?.error || '';
        
        // 1.5.7: 修复 Bug - 区分"用户已是成员"和"邀请已被使用"两种错误
        // isAlreadyMemberError: 当前用户已经是该组织的成员（可以显示成功）
        // isInviteUsedError: 邀请已达到使用次数限制（可能是其他用户使用的，不能假设当前用户已加入）
        const isAlreadyMemberError = errorMsg.includes('already a member') || 
                                     errorMsg.includes('已经是成员');
        // 1.5.7: 删除 isInviteUsedError 的处理 - 邀请被用完不代表当前用户已加入
        
        const currentUser = await getCurrentUser();
        // 1.5.7: 只有当错误明确表示"当前用户已是成员"时，才检查并显示成功界面
        // 不再处理 isInviteUsedError，因为邀请可能是被其他用户使用完的
        if (currentUser && isAlreadyMemberError) {
          const { organizations } = await getUserOrganizations();
          
          if (organizations.length > 0) {
            // 1.5.7: 尝试多种方式匹配目标组织
            const targetOrgId = info?.organization_id;
            const targetOrgName = info?.organization_name;
            
            let matchedOrg = null;
            
            // 优先使用 organization_id 精确匹配
            if (targetOrgId) {
              matchedOrg = organizations.find(org => org.id === targetOrgId);
            }
            
            // 1.5.8: 如果 organization_id 不可用，回退到 organization_name 匹配
            // 这处理了数据库响应不完整的情况
            if (!matchedOrg && targetOrgName) {
              matchedOrg = organizations.find(org => org.name === targetOrgName);
            }
            
            if (matchedOrg) {
              setAlreadyJoined(true);
              setJoinedOrgInfo(matchedOrg);
              setIsCheckingInvite(false);
              return;
            }
          }
        }
        
        // 如果用户未登录或没有组织，显示错误
        setError(translateApiError(errorMsg || t('invite.inviteExpired', '邀请链接已过期或无效'), t));
        setIsCheckingInvite(false);
        return;
      }

      setInviteInfo(info);
      setIsCheckingInvite(false);
    };

    verifyInvitation();
  }, [token, t]);

  // 接受邀请
  // 1.4.5: 接受邀请后自动切换到新组织
  const handleAcceptInvitation = async () => {
    if (!token) return;

    setIsAccepting(true);
    const { result, error: acceptError } = await acceptInvitation(token);

    if (acceptError) {
      // 1.5.3: 使用翻译函数转换 API 错误消息
      toast.error(translateApiError(acceptError.message, t));
      setIsAccepting(false);
      // 1.5.4: 重置 ref，允许用户重试
      acceptanceInitiatedRef.current = false;
      return;
    }

    if (result?.success) {
      // 1.4.5: 设置新加入的组织为当前组织
      if (result.organization_id) {
        setCurrentOrganization(result.organization_id);
      }
      toast.success(t('invite.joinedOrganization', '已成功加入组织'));
      // 1.5.3: 使用强制刷新跳转，确保 App.tsx 重新加载 onboarding 状态
      // 避免 React Router navigate 导致的状态不同步问题
      window.location.href = '/app';
    } else {
      toast.error(result?.message || t('invite.acceptFailed', '加入失败'));
      setIsAccepting(false);
      // 1.5.4: 重置 ref，允许用户重试
      acceptanceInitiatedRef.current = false;
    }
  };

  // 邮箱登录
  // 1.5.1: 保存 pendingInviteToken，确保登录后能正确处理邀请
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsSubmitting(true);

    // 1.5.1: 保存邀请 token，登录成功后自动处理
    localStorage.setItem('pendingInviteToken', token || '');

    const { error } = await signInWithEmail(email, password);
    
    if (error) {
      // 1.5.3: 使用翻译函数转换错误消息
      setAuthError(translateAuthError(error.message, t));
      setIsSubmitting(false);
      // 登录失败时清除 token
      localStorage.removeItem('pendingInviteToken');
    }
    // 登录成功后会触发 onAuthStateChange，App.tsx 会处理跳转到邀请页面
  };

  // 邮箱注册
  // 1.5.1: 保存 pendingInviteToken，确保邮件确认后能正确处理邀请
  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);

    // 验证密码强度
    const pwValidation = validatePasswordStrength(password);
    if (!pwValidation.isValid) {
      setAuthError(t('passwordRequirement'));
      return;
    }

    setIsSubmitting(true);

    // 1.5.1: 保存邀请 token，邮件确认后自动处理
    localStorage.setItem('pendingInviteToken', token || '');

    const { error } = await signUpWithEmail(email, password);
    
    if (error) {
      // 1.5.3: 使用翻译函数转换错误消息
      setAuthError(translateAuthError(error.message, t));
      setIsSubmitting(false);
      // 注册失败时清除 token
      localStorage.removeItem('pendingInviteToken');
    } else {
      toast.success(t('invite.checkEmailForConfirmation', '请检查邮箱完成验证'));
      setIsSubmitting(false);
      // 注意：不清除 pendingInviteToken，邮件确认后会使用
    }
  };

  // Google SSO 登录
  const handleGoogleLogin = async () => {
    setAuthError(null);
    // 保存邀请 token，登录成功后自动处理
    localStorage.setItem('pendingInviteToken', token || '');
    await signInWithGoogle();
  };

  // 加载中
  if (isCheckingAuth || isCheckingInvite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center relative">
        {/* 右上角语言切换 */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('invite.verifyingInvite', '正在验证邀请...')}</p>
        </div>
      </div>
    );
  }

  // 1.5.6: 已成功加入组织（多窗口场景：另一个窗口已处理邀请）
  if (alreadyJoined && joinedOrgInfo) {
    // 尝试关闭当前 tab 的处理函数
    const handleCloseTab = () => {
      // 设置当前组织
      setCurrentOrganization(joinedOrgInfo.id);
      
      // 尝试关闭当前 tab
      // 注意：只有当页面是由脚本打开时，window.close() 才会生效
      // 否则会跳转到项目页面
      try {
        window.close();
        // 如果 window.close() 没有生效（页面不是由脚本打开的），等待一小段时间后跳转
        setTimeout(() => {
          window.location.href = '/app';
        }, 100);
      } catch (e) {
        // 如果关闭失败，跳转到项目页面
        window.location.href = '/app';
      }
    };

    // 1.5.6: 按照 YUI 主题设计规范调整样式
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4 relative">
        {/* 右上角语言切换 */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          {/* 头部 - 紫色渐变（YUI 主题） */}
          <div className="bg-gradient-to-r from-primary to-purple-600 p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-8 h-8" />
              <h1 className="text-xl font-bold">{t('invite.alreadyJoined', '已成功加入组织')}</h1>
            </div>
            <p className="text-purple-100 text-sm">
              {t('invite.alreadyJoinedDesc', '您已通过其他窗口成功加入组织')}
            </p>
          </div>

          {/* 组织信息 */}
          <div className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{joinedOrgInfo.name}</h2>
                <p className="text-sm text-gray-500">
                  {t('invite.joinedAs', '您的角色')}: <span className="text-primary font-medium">{joinedOrgInfo.role === 'owner' ? t('invite.roleOwner', '所有者') : t('invite.roleMember', '成员')}</span>
                </p>
              </div>
            </div>

            {/* 提示信息 - 紫色系（YUI 主题） */}
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-100 mb-6">
              <p className="text-sm text-purple-700">
                🎉 {t('invite.closeTabHint', '您可以关闭此窗口，在另一个窗口中继续使用')}
              </p>
            </div>

            {/* 确认按钮 - 主按钮样式（YUI 主题） */}
            <button
              onClick={handleCloseTab}
              className="w-full px-6 py-3 bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              {t('invite.confirmAndClose', '确认')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 邀请无效
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4 relative">
        {/* 右上角语言切换 */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('invite.inviteInvalid', '邀请链接无效')}
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            {t('invite.backToHome', '返回首页')}
          </button>
        </div>
      </div>
    );
  }

  // 已登录 - 显示接受邀请确认
  if (user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4 relative">
        {/* 右上角语言切换 */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
          {/* 头部 */}
          <div className="bg-gradient-to-r from-primary to-purple-600 p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-8 h-8" />
              <h1 className="text-xl font-bold">{t('invite.joinOrganization', '加入组织')}</h1>
            </div>
            <p className="text-purple-100 text-sm">
              {t('invite.invitedToJoin', '您被邀请加入')} <span className="font-semibold text-white">{inviteInfo?.organization_name}</span>
            </p>
          </div>

          {/* 邀请信息 */}
          <div className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{inviteInfo?.organization_name}</h2>
                <p className="text-sm text-gray-500">
                  {t('invite.willJoinAs', '您将以')} <span className="text-primary font-medium">{inviteInfo?.role === 'owner' ? t('invite.roleOwner', '所有者') : t('invite.roleMember', '成员')}</span> {t('invite.roleJoin', '身份加入')}
                </p>
              </div>
            </div>

            {/* 当前用户信息 */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">{t('invite.currentAccount', '当前账号')}</p>
              <p className="font-medium text-gray-900">{user.email}</p>
            </div>

            {/* 邀请到期时间 */}
            {inviteInfo?.expires_at && (
              <p className="text-xs text-gray-400 mb-6 text-center">
                {t('invite.inviteExpiresAt', '邀请有效期至')}: {new Date(inviteInfo.expires_at).toLocaleDateString()}
              </p>
            )}

            {/* 操作按钮 */}
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/')}
                disabled={isAccepting}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('invite.decline', '拒绝')}
              </button>
              <button
                onClick={handleAcceptInvitation}
                disabled={isAccepting}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isAccepting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {t('invite.acceptInvite', '接受邀请')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 未登录 - 显示邀请信息 + 登录/注册表单
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4 relative">
      {/* 右上角语言切换 */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
        {/* 头部 - 邀请信息 */}
        <div className="bg-gradient-to-r from-primary to-purple-600 p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-8 h-8" />
            <h1 className="text-xl font-bold">{t('invite.joinOrganization', '加入组织')}</h1>
          </div>
          <p className="text-purple-100 text-sm">
            {t('invite.invitedToJoin', '您被邀请加入')} <span className="font-semibold text-white">{inviteInfo?.organization_name}</span>
          </p>
        </div>

        {/* 组织信息卡片 */}
        <div className="px-6 pt-6">
          <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl border border-purple-100">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">{inviteInfo?.organization_name}</h2>
              <p className="text-sm text-gray-500">
                {t('invite.roleLabel', '角色')}: <span className="text-primary font-medium">{inviteInfo?.role === 'owner' ? t('invite.roleOwner', '所有者') : t('invite.roleMember', '成员')}</span>
              </p>
            </div>
          </div>
        </div>

        {/* 登录/注册表单 */}
        <div className="p-6">
          <p className="text-center text-gray-600 mb-4">
            {t('invite.loginToAcceptInvite', '登录或注册以接受邀请')}
          </p>

          {/* 切换登录/注册 */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              onClick={() => setAuthMode('login')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                authMode === 'login' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('invite.loginTab', '登录')}
            </button>
            <button
              onClick={() => setAuthMode('register')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                authMode === 'register' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('invite.registerTab', '注册')}
            </button>
          </div>

          {/* Google SSO */}
          <button
            onClick={handleGoogleLogin}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors mb-4"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-gray-700 font-medium">
              {authMode === 'login' ? t('invite.continueWithGoogle', '使用 Google 登录') : t('invite.signUpWithGoogle', '使用 Google 注册')}
            </span>
          </button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t('invite.or', '或')}</span>
            </div>
          </div>

          {/* 邮箱表单 */}
          <form onSubmit={authMode === 'login' ? handleEmailLogin : handleEmailRegister}>
            {/* 邮箱输入 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invite.email', '邮箱')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('invite.enterEmail', '请输入邮箱')}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* 密码输入 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invite.password', '密码')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={authMode === 'register' ? t('invite.setPassword', '设置密码（至少8位，含字母+数字+符号）') : t('invite.enterPassword', '请输入密码')}
                  required
                  minLength={authMode === 'register' ? PASSWORD_MIN_LENGTH : undefined}
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {authMode === 'register' && (
                <div className="mt-1.5 space-y-0.5">
                  {[
                    { key: 'length', label: t('passwordRules.length', '至少8个字符') },
                    { key: 'letter', label: t('passwordRules.letter', '包含字母') },
                    { key: 'digit', label: t('passwordRules.digit', '包含数字') },
                    { key: 'special', label: t('passwordRules.special', '包含特殊符号（如 !@#$%）') },
                  ].map(rule => (
                    <p key={rule.key} className={`text-xs flex items-center gap-1 ${
                      passwordChecks[rule.key as keyof typeof passwordChecks]
                        ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      <span>{passwordChecks[rule.key as keyof typeof passwordChecks] ? '✓' : '○'}</span>
                      {rule.label}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* 错误提示 */}
            {authError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-sm text-red-600">{authError}</p>
              </div>
            )}

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {authMode === 'login' 
                    ? t('invite.loginAndJoin', '登录并加入') 
                    : t('invite.registerAndJoin', '注册并加入')
                  }
                </>
              )}
            </button>
          </form>

          {/* 邀请到期时间 */}
          {inviteInfo?.expires_at && (
            <p className="text-xs text-gray-400 mt-4 text-center">
              {t('invite.inviteExpiresAt', '邀请有效期至')}: {new Date(inviteInfo.expires_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
